import { useEffect, useRef, useState } from 'react';

const BLOBS = [
  {
    id: 'aqua',
    color: '68, 178, 168',   // deep teal-aqua
    size: 560,
    x: 20,
    y: 20,
    animClass: 'blob-1',
    bandMult: 0.95,
  },
  {
    id: 'cornflower',
    color: '105, 138, 205',  // soft cornflower blue
    size: 520,
    x: 65,
    y: 60,
    animClass: 'blob-2',
    bandMult: 1.12,
  },
  {
    id: 'sage',
    color: '112, 168, 138',  // muted sage green
    size: 540,
    x: 25,
    y: 68,
    animClass: 'blob-3',
    bandMult: 1.0,
  },
  {
    id: 'rose',
    color: '198, 142, 148',  // dusty rose
    size: 500,
    x: 70,
    y: 18,
    animClass: 'blob-4',
    bandMult: 0.88,
  },
];

export default function PlayerScreen({
  track,
  toggle,
  isPlaying,
  isLoading,
  analyserRef,
  hasEnded,
  onBack,
}) {
  const [titleVisible, setTitleVisible] = useState(true);
  const blobRefs = useRef([]);
  const animFrameRef = useRef(null);

  // Fade the track title out after 3 seconds
  useEffect(() => {
    const t = setTimeout(() => setTitleVisible(false), 3000);
    return () => clearTimeout(t);
  }, []);

  // Drive blob scale from audio amplitude via requestAnimationFrame
  useEffect(() => {
    if (!isPlaying || !analyserRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      blobRefs.current.forEach((el) => {
        if (el) el.style.setProperty('--audio-scale', '1');
      });
      return;
    }

    const analyser = analyserRef.current;
    const data = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      analyser.getByteFrequencyData(data);

      // Overall average amplitude across all frequency bins
      let sum = 0;
      for (let j = 0; j < data.length; j++) sum += data[j];
      const overall = sum / data.length;

      blobRefs.current.forEach((el, i) => {
        if (!el) return;
        const mult = BLOBS[i]?.bandMult ?? 1;
        const scale = 1 + (overall / 255) * 0.22 * mult;
        el.style.setProperty('--audio-scale', scale.toFixed(3));
      });

      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying, analyserRef]);

  return (
    <div className="player-screen">
      {BLOBS.map((blob, i) => (
        <div
          key={blob.id}
          ref={(el) => (blobRefs.current[i] = el)}
          className={`blob ${blob.animClass}`}
          style={{
            width: blob.size,
            height: blob.size,
            left: `${blob.x}%`,
            top: `${blob.y}%`,
            background: `radial-gradient(circle at 40% 40%,
              rgba(${blob.color}, 0.68) 0%,
              rgba(${blob.color}, 0.48) 38%,
              rgba(${blob.color}, 0.16) 62%,
              rgba(${blob.color}, 0.00) 74%)`,
            filter: 'blur(8px)',
            boxShadow: `
              0 0 22px 8px rgba(${blob.color}, 0.42),
              0 0 60px 24px rgba(${blob.color}, 0.18),
              inset 0 0 40px rgba(${blob.color}, 0.16)
            `,
          }}
        />
      ))}

      <button className="player-back" onClick={onBack} aria-label="Back">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <polyline points="15,18 9,12 15,6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div className={`player-title ${titleVisible ? '' : 'hidden'}`}>
        {track.title}
      </div>

      {hasEnded && (
        <div className="completion-message">
          Your meditation is complete.
        </div>
      )}

      <button
        className={`player-control ${isLoading ? 'loading' : ''}`}
        onClick={toggle}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isLoading ? (
          <span className="spinner" aria-hidden="true" />
        ) : isPlaying ? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="5" y="3" width="4" height="18" rx="1" />
            <rect x="15" y="3" width="4" height="18" rx="1" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </button>
    </div>
  );
}
